import AppButton from '@nxsys/components/AppButton';
import AppCard from '@nxsys/components/AppCard';
import AppPageTitle from '@nxsys/components/AppPageTitle';
import AppsContainer from '@nxsys/components/AppsContainer';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppsHeader from '@nxsys/components/AppsContainer/AppsHeader';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import { useIsAllowedTimesheetCreate } from '@nxsys/hooks/useEmployeePermission';
import { useNavigateWithQuery } from '@nxsys/hooks/useNavigateWithQuery';
import useNotificationSocket from '@nxsys/hooks/useNotificationSocket';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { TimesheetsState } from '@nxsys/store/slices/timesheets/reducer/timesheetsSlice';
import { GetListTimesheetsParams } from '@nxsys/store/slices/timesheets/type';
import { TimeSheetDataItem } from '@nxsys/types/models/timesheet';
import { getTaxWeekTaxYear, showCurrencyByCountry } from '@nxsys/utils';
import { csvConfigExport } from '@nxsys/utils/export';
import { showMessage } from '@nxsys/utils/message';
import { formatMoney } from '@nxsys/utils/number-helper';
import { ExportToCsv } from 'export-to-csv';
import { toNumber } from 'lodash';
import moment from 'moment';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useTheme } from 'styled-components';
import HeaderListing from '../components/HeaderListing';
import { getTimesheetEmployeeColumns } from './column';
import { PlusOutlined } from '@ant-design/icons';
import { IconArrowExport } from '@nxsys/components/AppIconSvg';

interface ITimesheetsEmployeeListingProps {
  timesheetsData: Pick<
    TimesheetsState['timesheetsData'],
    'list' | 'pagination' | 'queryParams'
  >;
  loading: boolean;
  getTimeSheetList: (params: GetListTimesheetsParams) => void;
}

const TimesheetsEmployeeListing: FC<ITimesheetsEmployeeListingProps> = ({
  timesheetsData,
  loading,
  getTimeSheetList,
}) => {
  const { messages } = useTypedIntl();
  const navigate = useNavigateWithQuery();
  const theme = useTheme();
  const location = useLocation();
  const canCreateTimesheet = useIsAllowedTimesheetCreate();

  const { list, pagination, queryParams } = timesheetsData;
  const { payrollCountry, user, isBureau } = useSelector(
    (state: AppState) => state.appStore,
  );

  const [selectedRows, setSelectedRows] = useState<TimeSheetDataItem[]>([]);

  const renderTitle = useCallback(() => {
    return (
      <AppPageTitle
        prefix={messages['timesheet.allTimesheet']}
        separator=""
        title={''}
      />
    );
  }, [messages]);

  const renderRightContent = () => {
    return (
      <div className="flex items-center gap-x-2">
        <AppButton
          name={messages['common.export']}
          variant="outlined"
          type="primary"
          leftIcon={<IconArrowExport />}
          onClick={() => handleExportSelected()}
        />
        {canCreateTimesheet && (
          <AppButton
            name={messages['common.addTimesheet']}
            type="primary"
            leftIcon={<PlusOutlined />}
            onClick={() => navigate('/timesheets-employee/add-new')}
          />
        )}
      </div>
    );
  };

  const refetchTimesheetList = () => {
    getTimeSheetList({ ...queryParams });
  };

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    getTimeSheetList({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const handleFilter = (params: GetListTimesheetsParams) => {
    getTimeSheetList({
      ...queryParams,
      ...params,
      page: 0,
    });
  };

  const handleExport = (data: TimeSheetDataItem[]) => {
    const dataExport = data.map((item) => {
      return {
        agencyRef: item?.subcontractorRef
          ? `${item?.subcontractorRef?.primaryAgency?.agencyPrefix || '___'}/${
              item?.subcontractorRef?.agencyRef || '___'
            }`
          : item?.starter
            ? item?.starter.agencyRef
            : '___',
        ...(isBureau
          ? {
              employeeId: item?.subcontractorRef?.id || '',
            }
          : {}),
        ...(!isBureau
          ? {
              assignmentName: item?.assignmentRef?.assignmentName || '',
            }
          : {}),
        status: item?.status || '',
        id: item?.id,
        ...(!isBureau
          ? {
              agencyName: item?.agencyRef?.name || '',
            }
          : {}),
        name: `${item?.subcontractorRef?.forename || ''} ${
          item?.subcontractorRef?.surname || ''
        }`,
        endDate: item?.endDate
          ? moment(item?.endDate).tz('Europe/London').format('DD/MM/YYYY')
          : '',
        batch: item?.batch || '',
        taxPeriod: item?.taxPeriod || '',
        taxYear: item?.taxYear || item?.currentYear || '',
        totalNet: formatMoney(
          toNumber(item?.totalNet) || 0,
          showCurrencyByCountry(payrollCountry),
        ),
        totalVat: formatMoney(
          toNumber(item?.totalVat) || 0,
          showCurrencyByCountry(payrollCountry),
        ),
        totalPrices: formatMoney(
          toNumber(item?.totalPrices) || 0,
          showCurrencyByCountry(payrollCountry),
        ),
        receipt: formatMoney(
          toNumber(item?.receipt) || 0,
          showCurrencyByCountry(payrollCountry),
        ),
        outstanding: formatMoney(
          toNumber(item?.outstanding) || 0,
          showCurrencyByCountry(payrollCountry),
        ),
        ...(!isBureau ? { invoiceNo: item?.invoiceNo || '' } : {}),
      };
    });

    const headers = [
      isBureau ? 'Employer Ref' : 'Agency Ref',
      ...(isBureau ? ['Employee ID'] : []),
      ...(!isBureau ? ['Assignment Name'] : []),
      'Status',
      'Timesheet ID',
      ...(!isBureau ? ['Agency Name'] : []),
      'Employee Name',
      'Week End Date',
      'Batch #',
      'Tax Period',
      'Tax Year',
      'Net',
      'VAT',
      'Total',
      'Receipt',
      'Outstanding',
      ...(!isBureau ? ['Sales INV Ref'] : []),
    ];

    const exportName = csvConfigExport(`Timesheet-export`, headers);
    const csvExporter = new ExportToCsv(exportName);
    csvExporter.generateCsv(dataExport);
  };

  const handleView = useCallback(
    (id: string) => {
      navigate(`/timesheets-employee/${id}`, {
        query: {
          from: location.pathname,
        },
      });
    },
    [navigate],
  );

  const handleExportSelected = () => {
    if (selectedRows.length === 0) {
      showMessage({
        type: 'warning',
        content: messages['common.noSelected'],
      });
      return;
    }
    handleExport(selectedRows);
  };

  useNotificationSocket({
    eventName: `reversal-payroll-from-timesheet-${user?.id}`,
    onSuccess: () => {
      showMessage({
        type: 'success',
        content: messages['timesheet.reverseSuccess'],
      });
      refetchTimesheetList();
    },
    onError: () => {
      showMessage({
        type: 'error',
        content: messages['timesheet.reverseFailed'],
      });
    },
  });

  useEffect(() => {
    getTimeSheetList({
      page: 0,
      size: 20,
      taxYear: getTaxWeekTaxYear().taxYear,
    });
  }, []);

  const onChangeTable = (sorter?: { field: string; order: 'desc' | 'asc' }) => {
    if (sorter?.order) {
      handleFilter({
        ...queryParams,
        page: 0,
        sortField: sorter.field || '',
        sortType: sorter.order,
      });
    } else {
      handleFilter({
        ...queryParams,
        page: 0,
        sortField: undefined,
        sortType: undefined,
      });
    }
  };

  return (
    <AppsContainer
      fullView
      type="bottom"
      title={renderTitle()}
      rightContent={renderRightContent()}
    >
      <AppCard>
        <AppsHeader>
          <HeaderListing onFilter={handleFilter} />
        </AppsHeader>
        <AppsContent>
          <AppTableContainer
            rowKey="id"
            data={list ?? []}
            loading={loading}
            page={pagination.current}
            total={pagination.totalDocs}
            pageSize={pagination.pageSize}
            scroll={{ x: 1200, y: 700 }}
            titleEmpty={messages['timesheet.empty']}
            columns={getTimesheetEmployeeColumns({
              messages,
              payrollCountry,
              handleView,
              theme,
            })}
            onChangePagination={onChangePagination}
            onChangeTable={onChangeTable}
            rowSelection={{
              type: 'checkbox',
              onChange: (_: React.Key[], selectedRows: any[]) => {
                setSelectedRows(selectedRows);
              },
            }}
          />
        </AppsContent>
      </AppCard>
    </AppsContainer>
  );
};

export default React.memo(TimesheetsEmployeeListing);
